/**
 * @fileoverview ExitButton component for confirming exit from the assignment
 * @example
 * // Usage in a component:
 * import { ExitButton } from './ExitButton'
 *
 * function Component() {
 *   return <ExitButton label="Exit" id="123" onSubmit={() => Promise.resolve()} />
 * }
 */

import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalContext } from '../ModalContext'
import { NavigationButton } from './NavigationButton'
import { PromptModalContent } from './questions/PromptModalContent'


/**
 * @interface ExitButtonProps
 * @description Props for the ExitButton component
 * @property {string} label - The label for the exit button
 * @property {string} id - The ID of the assignment
 * @property {boolean} confirmationNeeded - Whether confirmation is needed to exit
 * @property {Function} onSubmit - Callback function for submitting the exit
 */
interface ExitButtonProps {
    label: string
    id: string
    confirmationNeeded?: boolean
    onSubmit: (id: string, payload: Record<string, unknown>) => Promise<unknown>
}

/**
 * @function ExitButton
 * @description Component for confirming exit from the assignment
 * @param {ExitButtonProps} props - The props for the ExitButton component
 * @returns {JSX.Element} The ExitButton component
 */
export function ExitButton(props: ExitButtonProps) {
    const modal = useContext(ModalContext)
    const { t } = useTranslation()

    async function onExitWithoutConfirmation() {
        return props.onSubmit(props.id, { exit: true })
    }

    function onExitWithConfirmation(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        const target = e.target as HTMLButtonElement
        target.blur()

        modal.open({
            render: ({ onConfirm, onCancel }) => (
                <PromptModalContent
                    text={t('modals.exit.text')}
                    caption={t('modals.exit.caption')}
                    confirmText={t('modals.exit.buttons.confirm')}
                    cancelText={t('modals.exit.buttons.cancel')}
                    onCancel={onCancel}
                    onConfirm={async () => {
                        await onExitWithoutConfirmation()
                        await onConfirm()
                    }}
                />
            ),
        })
        return Promise.resolve()
    }

    return (
        <NavigationButton
            type="cancel"
            label={props.label}
            onClick={props.confirmationNeeded ? onExitWithConfirmation : onExitWithoutConfirmation}
        />
    )
}
